/**
 * SelectionModel
 *
 * Editor-layer multi-object selection. The AR runtime has no concept of
 * multi-selection — it tracks a single "primary" object on {@link TraceScene}
 * and clears it on play mode. This model layers a selection *set* on top of
 * that single primary so editors can drive multi-highlight and batch operations
 * (delete, duplicate, group) without touching the runtime.
 *
 * Design:
 * - **Primary** — the last-clicked object. Mirrored into `TraceScene` via
 *   `scene.select()`, so the existing `trace-viewer:selection-changed` event and
 *   every single-selection consumer (gizmo anchor, one-owner panels) keep working
 *   unchanged.
 * - **Set** — a superset that includes the primary plus any additively-selected
 *   objects. Editor-only; drives outline + batch ops.
 *
 * Invariants:
 * - The set always contains the primary, unless the set is empty (primary null).
 * - Holds {@link SceneObject} *references*, never names/ids (names are not stable).
 *
 * This model is the source of truth. It is driven explicitly by the editor
 * (`replace`/`add`/`toggle`/`clear`) rather than by auto-listening to scene
 * events, so the editor controls when a viewport/tree click is additive.
 */

import type { TraceScene } from '../../core/TraceScene';
import type { SceneObject } from '../../core/SceneObject';

export interface SelectionChange {
    /**
     * The full selection set at the moment of this change — a stable snapshot,
     * not a live view. Safe to retain (e.g. for undo history / later diffing);
     * subsequent model mutations won't alter it. Consistent with `added`/`removed`.
     */
    readonly set: ReadonlySet<SceneObject>;
    /** The primary object (last clicked), or null when the set is empty. */
    readonly primary: SceneObject | null;
    /** Objects added since the previous change. */
    readonly added: ReadonlySet<SceneObject>;
    /** Objects removed since the previous change. */
    readonly removed: ReadonlySet<SceneObject>;
}

type ChangeHandler = (change: SelectionChange) => void;

export class SelectionModel {
    private readonly scene: TraceScene;
    private readonly _set = new Set<SceneObject>();
    private _primary: SceneObject | null = null;
    private readonly _handlers = new Set<ChangeHandler>();

    constructor(scene: TraceScene) {
        this.scene = scene;
    }

    /** The primary (last-clicked) object, or null. Mirrors `scene.getSelectedObject()`. */
    get primary(): SceneObject | null {
        return this._primary;
    }

    /** Number of objects in the selection set. */
    get size(): number {
        return this._set.size;
    }

    /** Whether more than one object is selected. */
    get isMulti(): boolean {
        return this._set.size > 1;
    }

    /** Is the given object part of the selection set? */
    has(obj: SceneObject): boolean {
        return this._set.has(obj);
    }

    /** The selection set as an array, with the primary last (stable for callers). */
    list(): SceneObject[] {
        const out: SceneObject[] = [];
        for (const obj of this._set) {
            if (obj !== this._primary) out.push(obj);
        }
        if (this._primary) out.push(this._primary);
        return out;
    }

    /**
     * Replace the entire selection with a single object (or clear if null).
     * This is the normal single-click behavior — the set degenerates to one
     * object, preserving existing single-selection semantics.
     */
    replace(obj: SceneObject | null): void {
        // Clearing is the same operation with the same delta bookkeeping.
        if (obj === null) {
            this.clear();
            return;
        }
        const removed = new Set<SceneObject>();
        for (const existing of this._set) {
            if (existing !== obj) removed.add(existing);
        }
        const added = this._set.has(obj) ? new Set<SceneObject>() : new Set([obj]);

        this._set.clear();
        this._set.add(obj);
        this._setPrimary(obj, { added, removed });
    }

    /**
     * Add an object to the selection set and make it the primary.
     * No-op churn is avoided, but re-adding an existing member still promotes
     * it to primary.
     */
    add(obj: SceneObject): void {
        const added = this._set.has(obj) ? new Set<SceneObject>() : new Set([obj]);
        this._set.add(obj);
        this._setPrimary(obj, { added, removed: new Set() });
    }

    /**
     * Remove an object from the selection set. If it was the primary, the
     * primary falls back to another set member (or null when empty).
     */
    remove(obj: SceneObject): void {
        if (!this._set.delete(obj)) return;
        const removed = new Set([obj]);
        const nextPrimary = this._primary === obj ? this._lastMember() : this._primary;
        this._setPrimary(nextPrimary, { added: new Set(), removed });
    }

    /** Toggle membership: remove if present, otherwise add (and promote to primary). */
    toggle(obj: SceneObject): void {
        if (this._set.has(obj)) {
            this.remove(obj);
        } else {
            this.add(obj);
        }
    }

    /** Clear the entire selection. */
    clear(): void {
        if (this._set.size === 0 && this._primary === null) return;
        const removed = new Set(this._set);
        this._set.clear();
        this._setPrimary(null, { added: new Set(), removed });
    }

    /** Subscribe to selection changes. Returns an unsubscribe function. */
    onChanged(handler: ChangeHandler): () => void {
        this._handlers.add(handler);
        return () => this._handlers.delete(handler);
    }

    /**
     * Reconcile the selection against the live scene, dropping any object that
     * is no longer present (e.g. deleted). Editors should call this after
     * structural mutations. Emits a change only if something was pruned.
     */
    pruneMissing(): void {
        const live = new Set(this.scene.getSceneObjects());
        const removed = new Set<SceneObject>();
        for (const obj of this._set) {
            if (!live.has(obj)) removed.add(obj);
        }
        if (removed.size === 0) return;
        for (const obj of removed) this._set.delete(obj);
        const nextPrimary = this._primary && removed.has(this._primary) ? this._lastMember() : this._primary;
        this._setPrimary(nextPrimary, { added: new Set(), removed });
    }

    /** Most recently inserted set member (Set preserves insertion order), or null. */
    private _lastMember(): SceneObject | null {
        let last: SceneObject | null = null;
        for (const obj of this._set) last = obj;
        return last;
    }

    /**
     * Commit a new primary + emit. Mirrors the primary into the runtime scene
     * so the single-selection pipeline (event, gizmo, panels) stays in sync.
     */
    private _setPrimary(primary: SceneObject | null, delta: { added: ReadonlySet<SceneObject>; removed: ReadonlySet<SceneObject> }): void {
        this._primary = primary;
        // Mirror into the runtime. This fires `trace-viewer:selection-changed`,
        // which the selection bridge and single-selection consumers already track.
        //
        // We intentionally do NOT forward an `additive` flag here. `additive` is an
        // INPUT signal on the raw pointer path — it tells THIS model how to
        // interpret a click (add/toggle vs replace). Once the model has resolved
        // the selection, the authoritative "what changed" signal is this model's
        // own `onChanged` (with `added`/`removed`); the mirrored DOM event only
        // carries the resolved primary. So the mirror is always a plain select
        // (additive defaults to false), and consumers wanting set-level changes
        // subscribe to `onChanged`, not the DOM event's `additive`.
        if (this.scene.getSelectedObject() !== primary) {
            this.scene.select(primary);
        }
        this._emit(delta);
    }

    private _emit(delta: { added: ReadonlySet<SceneObject>; removed: ReadonlySet<SceneObject> }): void {
        const change: SelectionChange = {
            // Snapshot so a handler that retains the change sees the emission-time
            // set, not the model's later mutations (matches added/removed).
            set: new Set(this._set),
            primary: this._primary,
            added: delta.added,
            removed: delta.removed,
        };
        for (const handler of this._handlers) {
            handler(change);
        }
    }
}
